import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react'
import { Mtds } from 'prove-common'
import { ReferenceDialog } from './ReferenceDialog'
import { useBeforeUnload, useBlocker } from 'react-router'
import styles from './referenceData.module.css'
import {
  referenceErrorMessage,
  requireSuccessfulResponse
} from './referenceErrors'

export type ReferenceField = {
  key: string
  label: string
  required?: boolean
  maxLength?: number
  multiline?: boolean
  description?: string
}

type Props = {
  title: string
  fields: ReferenceField[]
  initialValues?: Record<string, string>
  onSave: (values: Record<string, string>) => Promise<unknown>
  onClose: () => void
}

export function ReferenceForm({
  title,
  fields,
  initialValues = {},
  onSave,
  onClose
}: Props) {
  const id = useId()
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map(({ key }) => [key, initialValues[key] ?? '']))
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState('')
  const [pending, setPending] = useState(false)
  const [discard, setDiscard] = useState(false)
  const submitting = useRef(false)
  const summary = useRef<HTMLHeadingElement>(null)
  const discardHeading = useRef<HTMLHeadingElement>(null)
  const dirty = fields.some(
    ({ key }) => values[key] !== (initialValues[key] ?? '')
  )
  const blocker = useBlocker(() => dirty || pending || submitting.current)

  useEffect(() => {
    if (blocker.state === 'blocked') discardHeading.current?.focus()
  }, [blocker.state])

  useBeforeUnload((event) => {
    if (dirty || pending || submitting.current) {
      event.preventDefault()
      event.returnValue = ''
    }
  })

  const dismiss = () => {
    if (submitting.current) return
    if (blocker.state === 'blocked') {
      blocker.reset()
      return
    }
    if (dirty) setDiscard(true)
    else onClose()
  }

  return (
    <ReferenceDialog
      className={styles.dialog}
      id={`${id}-dialog`}
      open
      aria-labelledby={`${id}-title`}
      closedby='closerequest'
      onClose={dismiss}
      onCancel={(event) => {
        event.preventDefault()
        dismiss()
      }}
    >
      <Mtds.Button
        type='button'
        command='request-close'
        commandfor={`${id}-dialog`}
        aria-label='Lukk'
        onClick={dismiss}
        disabled={pending}
      />
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault()
          if (submitting.current) return
          const cleaned = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [key, value.trim()])
          )
          const next: Record<string, string> = {}
          for (const field of fields) {
            if (field.required && !cleaned[field.key])
              next[field.key] = `${field.label} må fylles ut.`
            else if (
              field.maxLength &&
              cleaned[field.key].length > field.maxLength
            )
              next[field.key] =
                `${field.label} kan ha maksimalt ${field.maxLength} tegn.`
          }
          setErrors(next)
          setFailure('')
          setDiscard(false)
          if (Object.keys(next).length) {
            setTimeout(() => summary.current?.focus())
            return
          }
          submitting.current = true
          setPending(true)
          try {
            const result = await onSave(cleaned)
            // Every reference-data POST/PUT returns a JSON resource (201/200).
            // Shared hooks discard the response and can resolve undefined on an
            // empty HTTP error; that is never confirmation of a successful save.
            if (result == null)
              throw new Error('Mangler bekreftelse på lagring.')
            requireSuccessfulResponse(result)
            Mtds.toast.success('Oppføringen er lagret')
            if (blocker.state === 'blocked') blocker.reset()
            onClose()
          } catch (error) {
            setFailure(referenceErrorMessage(error))
            setTimeout(() => summary.current?.focus())
          } finally {
            submitting.current = false
            setPending(false)
          }
        }}
      >
        <Mtds.Grid data-gap='5'>
          <Mtds.Heading as='h2' data-size='md' id={`${id}-title`}>
            {title}
          </Mtds.Heading>
          <Mtds.Errorsummary hidden={!failure && !Object.keys(errors).length}>
            <Mtds.Heading as='h3' data-size='sm' ref={summary} tabIndex={-1}>
              Kunne ikke lagre
            </Mtds.Heading>
            {failure && <p>{failure}</p>}
            {!!Object.keys(errors).length && (
              <ul>
                {Object.entries(errors).map(([key, message]) => (
                  <li key={key}>
                    <a
                      href={`#${id}-${key}`}
                      onClick={(event) => {
                        event.preventDefault()
                        document.getElementById(`${id}-${key}`)?.focus()
                      }}
                    >
                      {message}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Mtds.Errorsummary>
          {fields.map((field) => (
            <Mtds.Field
              key={field.key}
              as={field.multiline ? 'textarea' : 'input'}
              id={`${id}-${field.key}`}
              name={field.key}
              label={field.label}
              required={field.required}
              maxLength={field.maxLength}
              description={field.description}
              validation={errors[field.key]}
              aria-invalid={!!errors[field.key]}
              disabled={pending}
              value={values[field.key]}
              onChange={(
                event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => setValues({ ...values, [field.key]: event.target.value })}
            />
          ))}
          {(discard || blocker.state === 'blocked') && (
            <Mtds.Alert data-color='warning'>
              <Mtds.Grid>
                <Mtds.Heading
                  as='h3'
                  data-size='sm'
                  ref={discardHeading}
                  tabIndex={-1}
                >
                  {pending
                    ? 'Lagring pågår. Vent før du forlater siden.'
                    : 'Du har ulagrede endringer. Vil du forkaste utkastet?'}
                </Mtds.Heading>
                <Mtds.Flex>
                  <Mtds.Button
                    type='button'
                    data-variant='secondary'
                    onClick={() => {
                      setDiscard(false)
                      if (blocker.state === 'blocked') blocker.reset()
                    }}
                  >
                    Fortsett redigering
                  </Mtds.Button>
                  <Mtds.Button
                    type='button'
                    disabled={pending}
                    onClick={() => {
                      if (submitting.current) return
                      if (blocker.state === 'blocked') blocker.proceed()
                      onClose()
                    }}
                  >
                    Forkast endringer
                  </Mtds.Button>
                </Mtds.Flex>
              </Mtds.Grid>
            </Mtds.Alert>
          )}
          <Mtds.Flex data-justify='end'>
            <Mtds.Button type='button' disabled={pending} onClick={dismiss}>
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              type='submit'
              data-variant='primary'
              disabled={pending}
              aria-busy={pending}
            >
              Lagre
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Grid>
      </form>
    </ReferenceDialog>
  )
}
